import * as THREE from "three";
import { Cell, Grid } from "@highmarch/domain";
import type { Biome } from "../../ui/chapters";
import { HQ_ROCKS } from "./common";
import { Contact, GroundContacts } from "../groundSlope";

/** The props' data and pure helpers (foliage.ts FoliageView, hubScenery.ts): pools, target heights, clearances, LOD. */

/** One biome's prop pools. */
export interface PropPool { rocks: string[]; trees: string[]; landmarks: string[]; dressing: string[] }

/** Tallest a prop may stand beside the fortress, in world units: a waist-high rock (the keep is ~1.4). */
export const BASE_CLEAR_HEIGHT = 0.65;

/** True when a family is known to be no taller than `max` (its Height target); unknown families count as tall. */
export function isLow(name: string, max = BASE_CLEAR_HEIGHT): boolean {
  const hit = PROP_HEIGHT.find(([re]) => re.test(name));
  return !!hit && hit[1] <= max;
}

/**
 * The portal clearance. A boss rises at the portal and the first thing it does is walk the first road cells, but a
 * tree one cell nearer the camera than the portal covers most of it at the camera's shallow 35° tilt (a 1.5-unit
 * oak one cell away hides everything under ~0.8 units), and the camera snaps round the map in four yaws, so any
 * side of the portal can be the near one. Props taller than `BaseClearHeight` within `PortalClear` cells of the
 * portal, and within `RoadClear` of the next `RoadClearCells` road cells, are hidden while the portal is there; a
 * tall blocker is swapped for a low rock (the cell is still blocked in the sim and must look it). The portal moves
 * as the road grows, so this is re-applied from `setRoad`; props are only ever hidden in place, never re-placed.
 */
export const PORTAL_CLEAR = 2;
export const ROAD_CLEAR = 1.5;
export const ROAD_CLEAR_CELLS = 2;

/** The cells kept clear for `road` (walking order: the portal first), see `PortalClear`. */
export function portalZone(road: readonly { x: number; y: number }[]): Set<string> {
  const zone = new Set<string>();
  const mark = (c: { x: number; y: number }, r: number) => {
    const R = Math.floor(r);
    for (let dz = -R; dz <= R; dz++) for (let dx = -R; dx <= R; dx++) if (dx * dx + dz * dz <= r * r + 1e-6) zone.add(`${c.x + dx}|${c.y + dz}`);
  };
  if (road.length > 0) mark(road[0], PORTAL_CLEAR);
  for (let i = 1; i <= ROAD_CLEAR_CELLS && i < road.length; i++) mark(road[i], ROAD_CLEAR);
  return zone;
}

/**
 * The low ones of `names` the library holds, else the low ones of the old HQ_ROCKS pool. The fallback is asked
 * only when the biome's own pool has none: `lib().hq` starts a background fetch of any shipped model the level's
 * asset set left out, so consulting HQ_ROCKS every time made a marsh level fetch p_rocks and p_crystals it never
 * drew (P1.11.1 / U2). `isLow` reads the anchors, which cost nothing, so it goes first.
 */
export function lowShipped(names: readonly string[], L: { hq(name: string): boolean }): string[] {
  const low = names.filter((n) => isLow(n) && L.hq(n));
  return low.length ? low : HQ_ROCKS.filter((n) => isLow(n) && L.hq(n));
}

/**
 * The whole-cell blockers, by name rather than by the `blocks` flag in anchors.json.
 *
 * The flag is stamped when a model is built, so a kit that replaces `p_rock_1` without re-flagging it silently
 * drops the new rock out of the pool and leaves the superseded one in — which is how the old kit's violet crystal
 * boulders kept appearing beside the new warm-brown ones. Naming the pool here makes the current kit the one that
 * ships.
 */
export const PROP_POOLS: Readonly<Record<Biome, PropPool>> = {
  meadow: {
    rocks: ["p_rock_1", "p_rock_2", "p_rock_spire", "p_boulder_big", "p_log", "p_stump"],
    trees: ["p_tree_oak", "p_tree_oak_big", "p_tree_oak_autumn", "p_tree_pair", "p_tree_pine_l", "p_tree_pine_m", "p_tree_dead"],
    landmarks: ["p_mesa_1", "p_mesa_2", "p_rock_arch", "p_rock_spire", "p_tree_oak_big", "p_tree_pine_l"],
    dressing: ["p_bush", "p_bush_berries", "p_grass_tall", "p_stump", "p_rocks", "p_rubble", "p_tree_pine_s", "p_log", "p_mushrooms", "p_bush_flowers"],
  },
  marsh: {
    rocks: ["p_marsh_rock_1", "p_marsh_rock_2", "p_rotten_stump", "p_log"],
    trees: ["p_cypress_1", "p_cypress_2", "p_dead_willow", "p_tree_dead"],
    landmarks: ["p_cypress_1", "p_cypress_2", "p_dead_willow", "p_rock_arch"],
    dressing: ["p_reeds", "p_lilypads", "p_rotten_stump", "p_witch_light", "p_mushrooms", "p_rubble"],
  },
  ashlands: {
    rocks: ["p_ash_rock_1", "p_ash_rock_2", "p_basalt_column_1", "p_basalt_column_2", "p_bone_pile"],
    trees: ["p_ash_tree_1", "p_ash_tree_2", "p_tree_dead"],
    landmarks: ["p_basalt_column_1", "p_basalt_column_2", "p_rock_spire", "p_ash_tree_1"],
    dressing: ["p_ash_drift", "p_bone_pile", "p_lava_vent", "p_rubble"],
  },
};

/**
 * Detail kept by a prop `out` cells beyond the map edge. The first cells out are in clear view, so they keep a
 * third of their triangles within a tight error; from where the haze begins (GroundMesh.FogStart) a tenth is
 * plenty — the whole prop is a smudge a few pixels across.
 */
export function farLod(out: number): { ratio: number; error: number } {
  return out < HAZE_FROM ? NEAR_LOD : HAZE_LOD;
}
/** Share of triangles kept and error allowed (relative to the prop's size) in clear view and in the haze. */
const NEAR_LOD = { ratio: 0.3, error: 0.02 };
const HAZE_LOD = { ratio: 0.1, error: 0.06 };
/** Cells beyond the edge where the haze begins to swallow the scenery (GroundMesh.FogStart). */
const HAZE_FROM = 4;

/**
 * The height each prop family is brought to, in world units, against the yardstick: a soldier is 0.33 and a cell
 * is 1. This is a TARGET, not a multiplier — the prop is measured and scaled to it. Successive art kits have
 * authored the same names at wildly different sizes (the v5 kit's grass was waist-high on a soldier, the AAA kit's
 * is not), and a multiplier silently inherits whatever the last kit happened to do. `d_` is exempt: the
 * micro-detail set is authored at final size on purpose.
 */
export const PROP_HEIGHT: readonly (readonly [RegExp, number])[] = [
  [/^p_tree_oak_big/, 1.55], [/^p_tree_oak|^p_tree_pair/, 1.25], [/^p_tree_pine_l/, 1.6], [/^p_tree_pine_m/, 1.25],
  [/^p_tree_pine_s/, 0.9], [/^p_tree_dead/, 1.3], [/^p_grass_tall/, 0.3], [/^p_flowers/, 0.22],
  [/^p_mushrooms/, 0.16], [/^p_bush/, 0.4], [/^p_mesa/, 1.7], [/^p_rock_spire|^p_rock_arch/, 1.15],
  [/^p_boulder_big/, 0.9], [/^p_rock/, 0.62], [/^p_stump/, 0.38], [/^p_log/, 0.36], [/^p_rubble|^p_rocks/, 0.28],
  // marsh
  [/^p_cypress/, 1.8], [/^p_dead_willow/, 1.55], [/^p_reeds/, 0.55], [/^p_lilypads/, 0.05],
  [/^p_marsh_rock/, 0.62], [/^p_rotten_stump/, 0.42], [/^p_witch_light/, 0.5],
  // ashlands
  [/^p_ash_tree/, 1.45], [/^p_basalt_column/, 1.4], [/^p_ash_rock/, 0.62], [/^p_ash_drift/, 0.25],
  [/^p_bone_pile/, 0.4], [/^p_lava_vent/, 0.33],
];
/** The scale to apply to a freshly cloned prop, or 1 when the family is authored at final size. */
export function scaleOf(name: string, root: THREE.Object3D): number {
  for (const [re, target] of PROP_HEIGHT) {
    if (!re.test(name)) continue;
    root.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(root);
    const h = box.max.y - box.min.y;
    return h > 1e-3 ? target / h : 1;
  }
  return 1;
}

/**
 * Registers the soft contact shadow a prop throws on the ground (terrain F4), sized from the family's height: a
 * tree's is half a cell across, a bush's a quarter. Flowers, grass and anything under 0.3 units cast none — at that
 * size a dark ring reads as a hole, not a shadow.
 */
export function propContact(name: string, at: THREE.Vector3): Contact | null {
  const hit = PROP_HEIGHT.find(([re]) => re.test(name));
  const h = hit ? hit[1] : 0.6;
  if (h < 0.3) return null;
  const tree = /tree|cypress|willow/.test(name);
  const c: Contact = { x: at.x, z: at.z, r: Math.min(0.6, 0.14 + h * 0.3), s: tree ? 0.42 : h < 0.5 ? 0.28 : 0.38 };
  GroundContacts.add(c);
  return c;
}

/** A placed prop, for the portal clearance (FoliageView.clearPortal). */
export interface Spot {
  readonly prop: THREE.Object3D;
  /** Its cell (x, y in grid terms). */
  readonly x: number;
  readonly z: number;
  /** Taller than `BaseClearHeight`: the kind of prop the clearance hides. */
  readonly tall: boolean;
  /** A whole-cell blocker (gets a low stand-in when hidden on the map). */
  readonly blocker: boolean;
  /** Scenery beyond the map edge. */
  readonly far: boolean;
  cleared: boolean;
  stand: THREE.Object3D | null;
  readonly contact: Contact | null;
}

/** The cells within `r` of the fortress's footprint on every side (FoliageView.setBase keeps them clear of tall props). */
export function baseRing(grid: Grid, r: number): Set<string> {
  const out = new Set<string>();
  for (const f of grid.footprint) {
    for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
      const c = new Cell(f.x + dx, f.y + dz);
      if (!grid.isBaseCell(c)) out.add(`${c.x}|${c.y}`);
    }
  }
  return out;
}
